import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import MuiLink from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { HelpCenterSection } from '../functions/groupHelpCenterCategories';
import { HelpCenterCrumb } from '../models/helpCenterCrumb';
import { HelpSearchField } from './helpSearchField';
import { HelpCenterFrame } from './helpCenterFrame';

type HelpCenterLayoutProps = {
  /** Brödsmulorna efter "Hjälpcenter". */
  trail: HelpCenterCrumb[];
  /** Huvudkategorin med sina kort, som visas i vänsterspalten. */
  section?: HelpCenterSection;
  currentCategoryId?: number;
  /** Sökningen som står i sökfältet, på resultatsidan. */
  searchValue?: string;
  children: ReactNode;
};

/**
 * Ramen för hjälpcentrets sidor under startsidan: brödsmulor överst, sökfältet och kategorierna i samma huvudkategori
 * till vänster och innehållet till höger. Spalten finns alltid, så att innehållet inte hoppar när kategorierna laddats.
 * På smala skärmar döljs spalten; sökfältet hamnar ovanför innehållet och brödsmulorna räcker för att hitta tillbaka.
 */
export const HelpCenterLayout = (props: HelpCenterLayoutProps) => {
  const { trail, section, currentCategoryId, searchValue, children } = props;
  const { t } = useTranslation();
  const theme = useTheme();
  // Ett enda sökfält: i spalten på breda skärmar, ovanför innehållet på smala där spalten är dold.
  const wide = useMediaQuery(theme.breakpoints.up('md'));

  return (
    <HelpCenterFrame>
      <GridWrapper>
        <Grid item xs={12}>
          <Breadcrumbs>
            <MuiLink component={Link} to={RoutesEnum.Help} underline='hover' color='inherit'>
              {t('common:helpLinkText')}
            </MuiLink>
            {trail.map(({ key, label, to }, i) =>
              to && i < trail.length - 1 ? (
                <MuiLink key={key} component={Link} to={to} underline='hover' color='inherit'>
                  {label}
                </MuiLink>
              ) : (
                <Typography key={key} color={i === trail.length - 1 ? 'text.primary' : 'inherit'}>
                  {label}
                </Typography>
              ),
            )}
          </Breadcrumbs>
        </Grid>
        {!wide && (
          <Grid item xs={12}>
            <HelpSearchField initialValue={searchValue} />
          </Grid>
        )}
        {/* Spalten har en fast bredd och innehållet tar resten av ramen; lite smalare spalt på mellanstora skärmar, där
            vänstermenyn tar plats. */}
        <Grid item sx={{ display: { xs: 'none', md: 'block' }, flex: { md: '0 0 220px', lg: '0 0 260px' } }}>
          {wide && <HelpSearchField initialValue={searchValue} />}
          <List dense disablePadding sx={{ mt: 2 }}>
            {section?.cards.map((category) => (
              <ListItemButton
                key={category.id}
                component={Link}
                to={`${RoutesEnum.HelpCategory}/${category.id}`}
                selected={category.id === currentCategoryId}
                sx={{ borderRadius: 1 }}
              >
                <ListItemText primary={category.name} />
              </ListItemButton>
            ))}
          </List>
        </Grid>
        <Grid item xs={12} md sx={{ minWidth: 0 }}>
          {/* Vit yta som husets andra innehållsytor, så att texten skiljs från bakgrunden. */}
          <Paper sx={{ position: 'relative', p: { xs: 2, lg: 4 } }}>{children}</Paper>
        </Grid>
      </GridWrapper>
    </HelpCenterFrame>
  );
};
